/**
 * Block visibility rule evaluator for the Studio preview. Mirrors
 * frontend/src/render/ruleEngine.ts byte-for-byte in semantics — two
 * copies exist because the Studio and the buyer frontend are separate
 * TypeScript projects (duplication is acceptable per CLAUDE.md §15-16).
 */

import type { Device, RuleClause, VisibilityRuleGroup } from "../../types/flow";

export interface PreviewEvalCtx {
  country_code: string;
  cart_total_cents: number;
  cart_skus: string[];
  cart_product_types: string[];
  cart_tags: string[];
  customer: "new" | "returning" | "logged_in";
  device: Device;
  has_vault_token: boolean;
  now: Date;
}

export const DEFAULT_PREVIEW_EVAL_CTX: PreviewEvalCtx = {
  country_code: "US",
  cart_total_cents: 11700,
  cart_skus: ["HOODIE-M", "CAP-ONESIZE"],
  cart_product_types: ["apparel"],
  cart_tags: ["demo"],
  customer: "new",
  device: "desktop",
  has_vault_token: false,
  now: new Date(),
};

function matchesClause(c: RuleClause, ctx: PreviewEvalCtx): boolean {
  switch (c.op) {
    case "country_is": return ctx.country_code === c.value;
    case "country_is_not": return ctx.country_code !== c.value;
    case "country_in": return c.value.includes(ctx.country_code);
    case "cart_total_gt": return ctx.cart_total_cents > c.value;
    case "cart_total_lt": return ctx.cart_total_cents < c.value;
    case "cart_contains_sku": return ctx.cart_skus.includes(c.value);
    case "cart_contains_product_type": return ctx.cart_product_types.includes(c.value);
    case "cart_contains_tag": return ctx.cart_tags.includes(c.value);
    case "customer_is": return ctx.customer === c.value;
    case "device_is": return ctx.device === c.value;
    case "has_vault_token": return ctx.has_vault_token;
    case "day_of_week": return c.value.includes(ctx.now.getDay() as 0 | 1 | 2 | 3 | 4 | 5 | 6);
    case "time_window": {
      const [sh, sm] = c.value.start_hhmm.split(":").map((n) => Number.parseInt(n, 10));
      const [eh, em] = c.value.end_hhmm.split(":").map((n) => Number.parseInt(n, 10));
      const mins = ctx.now.getHours() * 60 + ctx.now.getMinutes();
      const startMins = (sh || 0) * 60 + (sm || 0);
      const endMins = (eh || 0) * 60 + (em || 0);
      if (startMins <= endMins) return mins >= startMins && mins <= endMins;
      return mins >= startMins || mins <= endMins;
    }
    default: return true;
  }
}

export function evaluateVisibility(
  rules: VisibilityRuleGroup | undefined,
  ctx: PreviewEvalCtx,
): boolean {
  if (!rules) return true;
  const allOk = !rules.all || rules.all.every((c) => matchesClause(c, ctx));
  const anyOk = !rules.any || rules.any.length === 0 || rules.any.some((c) => matchesClause(c, ctx));
  return allOk && anyOk;
}
